import { logger } from '../../utils/logger.js'
import { authService } from '../auth/auth-service.js'
import {
  blockUser as apiBlockUser,
  unblockUser as apiUnblockUser,
  getBlockedUsers,
  type BlockResponse,
} from './moderation-api.js'

const log = logger.create('block-store')
const STORAGE_KEY = 'cairn_blocked_users'

class BlockStore {
  private static instance: BlockStore | undefined
  private blockedUserIds = new Set<string>()
  private initialized = false

  private constructor() {}

  static getInstance(): BlockStore {
    BlockStore.instance ??= new BlockStore()
    return BlockStore.instance
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const ids = JSON.parse(stored) as string[]
        this.blockedUserIds = new Set(ids)
      }
    } catch {
      // localStorage unavailable or corrupt
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.blockedUserIds]))
    } catch {
      // localStorage unavailable
    }
  }

  async initialize(): Promise<void> {
    if (this.initialized) return
    this.loadFromStorage()

    if (!authService.getAccessToken()) {
      this.initialized = true
      return
    }

    try {
      const blocked = await getBlockedUsers()
      this.blockedUserIds = new Set(blocked.map(b => b.userId))
      this.saveToStorage()
    } catch (err) {
      log.warn('Failed to sync blocked users from server:', err)
    }

    this.initialized = true
  }

  isBlocked(userId: string): boolean {
    if (!this.initialized) this.loadFromStorage()
    return this.blockedUserIds.has(userId)
  }

  async block(userId: string): Promise<BlockResponse> {
    const result = await apiBlockUser(userId)
    if (result.success) {
      this.blockedUserIds.add(userId)
      this.saveToStorage()
    }
    return result
  }

  async unblock(userId: string): Promise<boolean> {
    const result = await apiUnblockUser(userId)
    if (result.success) {
      this.blockedUserIds.delete(userId)
      this.saveToStorage()
    }
    return result.success
  }

  getBlockedList(): string[] {
    return [...this.blockedUserIds]
  }

  clear(): void {
    this.blockedUserIds.clear()
    this.initialized = false
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // localStorage unavailable
    }
  }
}

export const blockStore = BlockStore.getInstance()
